<template>
  <BorrowerAttributeConstructor
    :questionText="questionText"
    :description="description"
    :descriptionTooltip="descriptionTooltip"
    :tooltipText="tooltipText"
    :helpText="helpText"
    :disclaimer="disclaimer"
    :errorText="firstError"
    :label="label"
  >
    <template v-slot:input>
      <div role="group" :aria-labelledby="questionUniqueId+'-label'" class="p-0">
        <div :dreams-id="question.alias">
          <fieldset class="box-select-wrapper"
            :class="{
              'grid-col-2': question.metadata && question.metadata.columns === 2,
              'grid-col-4': question.metadata && question.metadata.columns === 4
            }"
          >
            <button
              v-for="(option, index) in options"
              type="button"
              role="checkbox"
              class="selection-pill text-center"
              :data-cy="'multi-select-widget-button-' + index"
              :class="{
                'selection-pill--selected': optionSelected(option),
                'selection-pill--invalid': v$.value && v$.value.$error,
                'selection-pill--icon': option.icon,
              }"
              :aria-checked="optionSelected(option) ? 'true' : 'false'"
              @click="onClick(option)"
              :key="index"
            >
              <NuxtImg
                v-if="option.icon"
                class="boolean-icon"
                :class="{'image-selected': optionSelected(option)}"
                :src="formatImagePath(option.icon)"/>
              <div v-if="option.icon" data-cy="multi-select-widget-image-container" class="image-container mb-2">
                &nbsp;
              </div>
              {{ option.label || option.name }}
            </button>
          </fieldset>
        </div>
      </div>
    </template>
  </BorrowerAttributeConstructor>
</template>

<script setup>
import { useVuelidate } from '@vuelidate/core'
import { requiredIf, helpers } from '@vuelidate/validators'
const { withMessage } = helpers
import formatImagePath from '~/libs/image-format'

const props = defineProps({
  question: Object,
})
const emit = defineEmits(['invalidity','answer'])

const state = reactive({
  value: props.question.value,
})
const rules = {
  value: { required: withMessage(
    'Please select a value.',
    requiredIf(() => !props.question.mustAllowNull)
  ) }
}
const v$ = useVuelidate(rules, state)

const invalid = computed(() => v$.value.$invalid)
const firstError = computed(() => v$.value.$errors?.[0]?.$message)
const options = computed(() => props.question.options)
const selectedOptions = computed(() => state.value ? state.value.split(',') : [])

const questionText = computed(() =>
  props.question.questionText || ''
)
const description = computed(() =>
  props.question.description || ''
)
const descriptionTooltip = computed(() =>
  props.question.descriptionTooltip || ''
)
const label = computed(() =>
  props.question.name || ''
)
const tooltipText = computed(() =>
  props.question.tooltip || props.question.helpText || ''
)
const helpText = computed(() =>
  props.question.tooltip ? props.question.helpText : ''
)
const disclaimer = computed(() =>
  props.question.disclaimer || ''
)
const questionRequired = computed(() =>
  !props.question.mustAllowNull
)
const questionUniqueId = computed(() =>
  props.question.alias && props.question.alias.replace(/[_\.]/g, '-')
)

const optionSelected = (option) => {
  return selectedOptions.value.includes(option.value)
}
const onClick = (option, event) => {
  if (event) {
    event.stopPropagation()
  }

  let selected

  if (optionSelected(option)) {
    selected = selectedOptions.value.filter(val => option.value !== val)
  } else {
    selected = selectedOptions.value.slice().concat([option.value])
  }

  state.value = selected.join(',')

  emit('answer', { value: selected.join(','), isInvalid: false })
}

watch(invalid, (invalid) => {
  emit('invalidity', invalid)
}, {immediate: true})

const questionValue = computed(() =>
  props.question.value
)
watch(questionValue, (value) => {
  state.value = value
})

</script>

<style scoped lang="scss">
.selection-pill {
  min-height: 48px;
  color: $type-400;
  line-height: 18px;
  font-size: 14px;

  border-radius: 4px;
  border: 1px solid $border-100;
  padding: 7px;
  position: relative;

  cursor: pointer;
  background-color: $white;
  .selection-pill--icon {
    padding: 10px 7px;
  }
}

.selection-pill:target, .selection-pill:focus, .selection-pill:hover {
  box-shadow: 0 0 0.25rem 0.125rem $neutral-100;
}
.selection-pill--selected {
  border: 2px solid $primary;
  padding: 6px;
  .selection-pill--icon {
    padding: 9px 6px;
  }
}
.selection-pill--invalid {
  border-color: $red;
}

.box-select-wrapper {
  display: grid;
  grid-template-columns: repeat(3, calc((100% - 1em) / 3));
  grid-gap: 0.5em;

  &.grid-col-2 {
    grid-template-columns: repeat(2, calc((100% - 0.5em) / 2));
  }
  &.grid-col-4 {
    grid-template-columns: repeat(4, calc((100% - 1.5em) / 4));
  }
}

.image-container {
  height: 50px;
  position: relative;
}
.boolean-icon {
  clip-path: polygon(50% 0%, 0% 0%, 0% 100%, 50% 100%);
  transform: translateX(-25%) translateY(5px);
  position: absolute;
  left: 50%;
  width: auto;
  max-width: calc(200% - 16px);
  height: 40px;

  &.image-selected {
    clip-path: polygon(50% 0%, 100% 0%, 100% 100%, 50% 100%);
    transform: translateX(-75%) translateY(5px);
  }
}
</style>
